/**
 * User Menu Component
 *
 * Dropdown menu for authenticated users showing:
 * - User avatar/initials
 * - Profile link
 * - Admin link (if admin)
 * - Sign out
 */

import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, AuthStates } from "../auth/AuthContext";
import { FaUser, FaCog, FaSignOutAlt, FaHome } from "react-icons/fa";
import "./UserMenu.scss";

const UserMenu = ({ className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const { user, userProfile, authState, isAdmin, signOut } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
    navigate("/");
  };

  // Don't show for anonymous or unauthenticated users
  if (authState !== AuthStates.AUTHENTICATED) {
    return (
      <div className={`user-menu ${className}`}>
        <Link to="/login" className="login-link">
          Sign&nbsp;In
        </Link>
      </div>
    );
  }

  // Get user initials for avatar
  const getInitials = () => {
    if (userProfile?.displayName) {
      return userProfile.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  return (
    <div className={`user-menu ${className}`} ref={menuRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">{getInitials()}</div>
        <span className="user-name">
          {userProfile?.displayName || user?.email?.split("@")[0]}
        </span>
        <svg
          className={`chevron ${isOpen ? "open" : ""}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <div className="user-avatar large">{getInitials()}</div>
            <div className="user-info">
              <span className="user-display-name">
                {userProfile?.displayName || "User"}
              </span>
              <span className="user-email">{user?.email}</span>
              {isAdmin && <span className="user-role">Administrator</span>}
            </div>
          </div>

          <div className="user-menu-divider" />

          <nav className="user-menu-nav">
            <Link
              to="/dashboard"
              className="user-menu-item"
              onClick={() => setIsOpen(false)}
            >
              <FaHome className="icon" />
              <span>Dashboard</span>
            </Link>

            <Link
              to="/profile"
              className="user-menu-item"
              onClick={() => setIsOpen(false)}
            >
              <FaUser className="icon" />
              <span>Profile</span>
            </Link>

            <Link
              to="/change-password"
              className="user-menu-item"
              onClick={() => setIsOpen(false)}
            >
              <FaCog className="icon" />
              <span>Change Password</span>
            </Link>
          </nav>

          <div className="user-menu-divider" />

          <button className="user-menu-item signout" onClick={handleSignOut}>
            <FaSignOutAlt className="icon" />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
